<template>
  <div>
  <el-tabs v-model="activeLanguage">
    <el-tab-pane v-for="domain in domains"
                 :key="domain.language"
                 :label="`Text ${domain.language.toUpperCase()}`"
                 :name="domain.language">
      <div class="card"
           v-for="text in entityTexts"
           :key="text.alias">
        <div class="card-header d-flex align-items-center">
          <h4 class="card-title mr-16 " style="margin-bottom: 0;"> {{ text.alias }} </h4>
          <button class="btn btn-primary btn-sm btn-image"
                  @click="goToAIText(text.alias, domain.language)"
                  v-if="defaultEntityParams.forEntity">
            <img src="@/assets/imgs/google-gemini-icon.png">
            Generate text
          </button>
        </div>
        <div class="card-body">
          <ckeditor v-model="text.text"
                    v-if="activeLanguage === domain.language"
                    :config="{ allowedContent: true }"
          ></ckeditor>
          <base-button type="primary"
                       class="mt-16"
                       :disabled="loadingTexts || saving"
                       @click="save(text)">Save {{ text.alias }}</base-button>
          <base-button v-if="text.alias === 'txt_after_3_clinics'"
                       type="primary"
                       class="mt-16 ml-16"
                       @click="isDialogOpen = true">Translate {{ text.alias }}</base-button>
        </div>
      </div>
    </el-tab-pane>
    <el-dialog :visible.sync="isDialogOpen">
      <h3>Please select languages for translate</h3>
      <el-checkbox-group v-model="translateLanguages" class="flex">
        <el-checkbox v-for="domain in domains" :key="domain.language" :label="domain.language" :value="domain.language" />
      </el-checkbox-group>
      <base-button
                  type="primary"
                  class="mt-16"
                  @click="translate('txt_after_3_clinics')">Translate</base-button>
    </el-dialog>
  </el-tabs>
  <TextRevisions :aliases="data.defaultTexts.map(text => text.alias)" :target="revisionTarget" :busy="loadingTexts || saving" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import APICmsCatalogs from '@/api/modules/cms-api-catalogs';
import TextRevisions from './TextRevisions.vue';

export default {
  props: ['data'],
  components: { TextRevisions },
  data: () => ({
    activeLanguage: null,
    entityTexts: [],
    isDialogOpen: false,
    translateLanguages: [],
    loadingTexts: false,
    saving: false,
    loadGeneration: 0,
  }),
  computed: {
    ...mapState('cmsApiCatalogs', ['domains']),
    defaultEntityParams() {
      return {
        lang: this.activeLanguage,
        entitiesString: this.data.entitiesString,
        entityId: this.$route.params.entityId,
        forEntity: this.data.forEntity,
        forEntityId: this.data.forEntityId,
      };
    },
    contextKey() { return JSON.stringify(this.defaultEntityParams); },
    revisionTarget() {
      const params = this.defaultEntityParams;
      if (!params.lang || !Number.isInteger(Number(params.entityId)) || Number(params.entityId) < 1) return null;
      const target = { kind: params.forEntity ? 'entity_text_set' : 'text_set', lang: params.lang, entity: params.entitiesString, entity_id: Number(params.entityId) };
      if (params.forEntity) {
        if (!Number.isInteger(Number(params.forEntityId)) || Number(params.forEntityId) < 1) return null;
        target.for_entity = params.forEntity;
        target.for_entity_id = Number(params.forEntityId);
      }
      return target;
    },
  },
  watch: {
    contextKey() {
      this.getEntityTextsEvent(this.defaultEntityParams);
    }
  },
  beforeDestroy() { this.loadGeneration++; },
  created() {
    this.activeLanguage = this.domains[0].language;
  },
  methods: {
    goToAIText(alias, lang) {
      this.$router.push({
        name: 'AITexts',
        query: {
          type: this.data.entitiesString,
          entityId: this.$route.params.entityId,
          countryId: this.data.forEntityId,
          alias,
          lang,
          textType: 'entity_text'
        }
      });
    },
    async save(data) {
      if (this.saving || this.loadingTexts) return;
      const key = this.contextKey;
      this.saving = true;
      this.$store.dispatch('setLoader', true);

      try {
        const response = await APICmsCatalogs[this.data.saveMethod]({
          ...this.defaultEntityParams,
          data: { alias: data.alias, text: data.text },
        });
        if (response.data && response.data.error) throw Error('save failed');
        if (key === this.contextKey) this.$notify({ type: 'success', title: 'Saved' });
      } catch (e) {
        this.$notify({ type: 'warning', title: 'Save Failed' });
      } finally {
        this.saving = false;
        this.$store.dispatch('setLoader', false);
      }
    },
    async translate(alias) {
      this.$store.dispatch('setLoader', true);

      try {
        await APICmsCatalogs[this.data.translateMethod]({
          ...this.defaultEntityParams,
          alias,
          target_locales: this.translateLanguages,
        });
        this.$notify({ type: 'success', title: 'Translated, wait a few minutes' });
      } catch (e) {
        console.log(e)
        this.$notify({ type: 'warning', title: 'Translate Failed' });
      } finally {
        this.$store.dispatch('setLoader', false);
        this.isDialogOpen = false;
      }
    },
    getEntityTextsEvent(params) {
      const generation = ++this.loadGeneration;
      this.entityTexts = [];
      if (!this.revisionTarget) { this.loadingTexts = false; return Promise.resolve(); }
      this.loadingTexts = true;
      const defaultTexts = this.data.defaultTexts.map(el => ({ ...el, text: '' }));
      return APICmsCatalogs[this.data.getMethod](params).then((response) => {
        if (generation !== this.loadGeneration) return;
        const result = response.data;
        const emptyCode = params.forEntity ? 'texts.entity_text.not_found' : 'texts.text.not_found';
        const savedTexts = Array.isArray(result) ? result :
          (result && result.error && result.error.code === emptyCode ? [] : null);
        if (savedTexts !== null) {
          this.entityTexts = defaultTexts.map(defaultText => {
            let findingEntityText = savedTexts.find(entityText => entityText.alias === defaultText.alias);
            return {
              alias: defaultText.alias,
              text: findingEntityText ? findingEntityText.text : '',
            };
          });
        } else { throw Error('invalid text response'); }
      }).catch(() => {
        if (generation === this.loadGeneration) this.$notify({ type: 'warning', title: 'Could not load texts' });
      }).finally(() => {
        if (generation === this.loadGeneration) this.loadingTexts = false;
      });
    },
  }
}
</script>
